import { CellType, EBlockWidth, NodeType, RowType, LinkBlockHelper, SlideShowHelper, Context, hasUsedPricingRows, } from '@getaccept/editor-lib-new';
import { defineComponent, computed } from 'vue';
import { CommentsHelper } from '@getaccept/lib-shared-new/src/contextual-commenting/helpers/comments.helper';
import { CreateContextualCommentButtonState } from '@getaccept/lib-shared-new/src/contextual-commenting/types/create-contextual-comment-button-state';
import DetachedCommentAnchor from '@getaccept/lib-shared-new/src/contextual-commenting/components/DetachedCommentAnchor.vue';
import EditorNodeVideo from './EditorNodeVideo.vue';
import EditorNodePricingTable from './pricing-table/EditorNodePricingTable.vue';
import EditorNodeText from './EditorNodeText.vue';
import EditorNodeTable from './EditorNodeTable.vue';
import EditorNodeImage from './EditorNodeImage.vue';
import EditorNodeForm from './EditorNodeForm.vue';
import EditorNodeDivider from './EditorNodeDivider.vue';
import EditorNodeLink from './EditorNodeLink.vue';
import EditorNodeSignature from './EditorNodeSignature.vue';
import EditorNodeContractLink from './contract-link/EditorNodeContractLink.vue';
import EditorNodeContentPlaceholder from './content-placeholder/EditorNodeContentPlaceholder.vue';
export default defineComponent({
    name: 'EditorCell',
    components: {
        DetachedCommentAnchor,
        EditorNodeDivider,
        EditorNodeImage,
        EditorNodeTable,
        EditorNodeText,
        EditorNodeLink,
        EditorNodeVideo,
        EditorNodeForm,
        EditorNodePricingTable,
        EditorNodeSignature,
        EditorNodeContractLink,
        EditorNodeContentPlaceholder,
    },
    props: {
        context: { type: String, default: () => Context.Document },
        nodes: { type: Array, default: () => [] },
        mergeValues: {
            type: Object,
            default: () => ({ document: {}, sender: {}, recipients: [], entity: {} }),
        },
        isPdfViewer: { type: Boolean },
        rowType: { type: String, default: RowType.Standard },
        parentFirstChild: { type: Boolean },
        parentLastChild: { type: Boolean },
        blockWidthClass: {
            type: String,
            default: null,
        },
        focusedRequiredId: { type: String, default: '' },
        cellWidth: { type: Number, default: 100 },
        pageWidth: { type: Number, default: 0 },
        isBlockToggled: { type: Boolean },
        cellType: {
            type: String,
            default: null,
        },
        sender: {
            type: Object,
            default: () => ({}),
        },
        recipient: {
            type: Object,
            default: () => ({}),
        },
        recipientInput: { type: Array, default: () => [] },
        updatingInputs: { type: Array, default: () => [] },
        failedInputUpdates: { type: Array, default: () => [] },
        theme: {
            type: Object,
            default: () => ({}),
        },
        currentSlide: { type: Number, default: 0 },
        isGridView: { type: Boolean },
        transitionName: { type: String, default: '' },
        partiallySignedFields: { type: Array, default: () => [] },
        pusherClient: { type: Object, default: undefined },
        comments: { type: Array, default: () => [] },
        selectedCommentId: { type: String, default: '' },
        showContextualComments: { type: Boolean, default: false },
        createContextualCommentButtonState: {
            type: String,
            default: CreateContextualCommentButtonState.Enabled,
        },
        currentUser: { type: Object, default: undefined },
        linkedContracts: { type: Array, default: () => [] },
        mappedPlaceholders: {
            type: Array,
            default: () => [],
        },
        allowedToEditAfterPartiallySigned: { type: Boolean },
    },
    emits: [
        'fetch-new-image',
        'observe-node-visibility',
        'click',
        'node-click',
        'track-video',
        'update-input-value',
        'select-comment',
        'create-comment',
        'partially-sign',
    ],
    setup(props) {
        const filterNodes = ({ type }) => {
            switch (props.cellType) {
                case CellType.ImageAndText:
                    return [NodeType.Image, NodeType.Text].includes(type);
                case CellType.ImageOnly:
                    return NodeType.Image === type;
                case CellType.TextOnly:
                    return NodeType.Text === type;
                default:
                    return false;
            }
        };
        const filteredNodes = computed(() => props.cellType ? props.nodes.filter(filterNodes) : props.nodes);
        const filteredNodeIds = computed(() => filteredNodes.value.map(node => node.id));
        const commentsWithoutNode = computed(() => props.comments.filter(comment => { var _a; return !filteredNodeIds.value.includes((_a = comment.editorSelection) === null || _a === void 0 ? void 0 : _a.nodeId); }));
        const isSmallScreen = computed(() => props.blockWidthClass === EBlockWidth.XXSmall ||
            props.blockWidthClass === EBlockWidth.XSmall);
        const widthStyle = computed(() => {
            if (isSmallScreen.value) {
                return 'width: 100%;';
            }
            else if (props.rowType === RowType.Columns) {
                return `width:calc(${props.cellWidth}% - 1rem)`;
            }
            return `width:${props.cellWidth}%`;
        });
        const usedPricingTableSections = (node) => node.pricingTableSections.filter(hasUsedPricingRows);
        const getLinkedContract = (node) => props.linkedContracts.find(({ id }) => id === node.contractId);
        const getContentPlaceholder = (nodeId) => props.mappedPlaceholders.find(({ id }) => id === nodeId);
        return {
            commentsWithoutNode,
            filterCommentsByNode: CommentsHelper.filterCommentsByNode,
            filteredNodes,
            filterNodes,
            getVisibleSlides: SlideShowHelper.getVisibleSlides,
            hasLinkNode: LinkBlockHelper.hasLinkNode,
            isSmallScreen,
            NodeType,
            RowType,
            widthStyle,
            usedPricingTableSections,
            getLinkedContract,
            CellType,
            getContentPlaceholder,
        };
    },
});
